import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../../contexts/ApiContext'
import { trackHomePageView, trackQuoteRequest, openChatWithContext } from '../../utils/eventTracking'
import { 
  Shield, 
  Heart, 
  Car, 
  Plane, 
  Home as HomeIcon,
  CheckCircle,
  Star,
  ArrowRight,
  Phone,
  Mail,
  MapPin
} from 'lucide-react'

const Home = () => {
  const { getPolicyTypes, getPolicyTypesByCategory } = useApi()
  const [policyTypes, setPolicyTypes] = useState([])
  const [coverageAmounts, setCoverageAmounts] = useState([])
  const [selectedPolicyType, setSelectedPolicyType] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedInsuranceType, setSelectedInsuranceType] = useState('')
  const [selectedCoverageAmount, setSelectedCoverageAmount] = useState('')
  const [availableCategories, setAvailableCategories] = useState([])
  const [availableInsuranceTypes, setAvailableInsuranceTypes] = useState([])
  const [loading, setLoading] = useState(true)

  // Track page view on mount - only once
  useEffect(() => {
    let mounted = true
    if (mounted) {
      trackHomePageView()
    }
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const fetchPolicyTypes = async () => {
      try {
        setLoading(true)
        const types = await getPolicyTypes()
        setPolicyTypes(types)
        
        // Extract unique product categories
        const categories = [...new Set(types.map(type => type.product_category).filter(Boolean))]
        setAvailableCategories(categories)
        
        // Extract unique insurance types for each category
        const insuranceTypesMap = {}
        types.forEach(type => {
          if (type.product_category) {
            if (!insuranceTypesMap[type.product_category]) {
              insuranceTypesMap[type.product_category] = new Set()
            }
            // Try different possible field names for insurance type
            const insuranceTypeName = type.policy_type_name || type.display_name || type.product_name || type.name
            if (insuranceTypeName) {
              insuranceTypesMap[type.product_category].add(insuranceTypeName)
            }
          }
        })
        
        // Convert sets to arrays
        const insuranceTypes = {}
        Object.keys(insuranceTypesMap).forEach(category => {
          insuranceTypes[category] = Array.from(insuranceTypesMap[category])
        })
        
        setAvailableInsuranceTypes(insuranceTypes)
        
        // Fallback data if no categories are found
        if (categories.length === 0) {
          setAvailableCategories(['Health Insurance', 'Life Insurance'])
          setAvailableInsuranceTypes({
            'Health Insurance': ['Individual Health', 'Family Health', 'Senior Citizen Health'],
            'Life Insurance': ['Term Life', 'Whole Life', 'Endowment']
          })
        }
        
        // Generate coverage amounts based on available policy types
        const amountsMap = new Map() // Use Map to store unique amounts by ROUNDED value (in lakhs)
        
        types.forEach(type => {
          if (type.base_sum_insured_min && type.base_sum_insured_max) {
            const min = Math.floor(type.base_sum_insured_min / 100000) * 100000
            const max = Math.floor(type.base_sum_insured_max / 100000) * 100000
            const step = 100000 // Fixed step of 1 Lakh for consistency
            
            for (let amount = min; amount <= max; amount += step) {
              const lakhs = Math.round(amount / 100000) // Round to nearest lakh
              const roundedAmount = lakhs * 100000 // Convert back to amount
              
              // Store by rounded amount to avoid duplicates like 4.4 Lakh and 4 Lakh
              if (!amountsMap.has(roundedAmount) && roundedAmount > 0) {
                amountsMap.set(roundedAmount, {
                  value: roundedAmount,
                  label: `₹${lakhs} Lakh`
                })
              }
            }
          }
        })
        
        // Convert Map to Array, sort, and limit
        const finalAmounts = Array.from(amountsMap.values())
          .sort((a, b) => a.value - b.value)
          .slice(0, 10)
        
        setCoverageAmounts(finalAmounts)
      } catch (error) {
        console.error('Error fetching policy types:', error)
        // Fallback to default values
        setCoverageAmounts([
          { value: 500000, label: '₹5 Lakh' },
          { value: 1000000, label: '₹10 Lakh' },
          { value: 2500000, label: '₹25 Lakh' },
          { value: 5000000, label: '₹50 Lakh' }
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchPolicyTypes()
  }, [getPolicyTypes])

  const features = [
    {
      icon: Shield,
      title: 'Health Insurance',
      description: 'Comprehensive health coverage for you and your family with cashless treatment',
      color: 'text-blue-600'
    },
    {
      icon: Heart,
      title: 'Life Insurance',
      description: 'Secure your family\'s future with comprehensive life insurance coverage',
      color: 'text-red-600'
    }
  ]

  const benefits = [
    '24/7 Customer Support',
    'Quick Claim Processing',
    'Cashless Hospital Network',
    'Online Policy Management',
    'Competitive Premiums',
    'Flexible Payment Options'
  ]

  const testimonials = [
    {
      name: 'Rajesh Sharma',
      location: 'Mumbai',
      rating: 5,
      text: 'Excellent service and quick claim settlement. Highly recommended!'
    },
    {
      name: 'Priya Patel',
      location: 'Delhi',
      rating: 5,
      text: 'The best insurance company I\'ve ever dealt with. Very professional.'
    },
    {
      name: 'Amit Kumar',
      location: 'Bangalore',
      rating: 5,
      text: 'Great coverage options and affordable premiums. Very satisfied.'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-center lg:text-left">
                Your Trusted
                <span className="block text-yellow-300">Insurance Partner</span>
              </h1>
              <p className="text-xl mb-8 text-blue-100 text-center lg:text-left">
                Comprehensive insurance solutions for health, life, motor, and travel. 
                Get the protection you need with competitive rates and excellent service.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/policies"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center"
                >
                  Browse Policies
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button
                  onClick={() => openChatWithContext('I would like to talk to an Advisor about insurance options.', 'home_hero')}
                  className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                  Talk to an Advisor
                </button>
              </div>
            </div>
            <div className="animate-slide-up">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold mb-4 text-gray-900 text-center">Get Instant Quote</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 text-left">Product Category</label>
                    <select 
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value)
                        setSelectedInsuranceType('') // Reset insurance type when category changes
                        setSelectedCoverageAmount('') // Reset coverage amount when category changes
                      }}
                      className="w-full p-4 rounded-xl bg-white border-2 border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-300 shadow-sm hover:shadow-md"
                    >
                      <option value="">Select Product Category</option>
                      {availableCategories.map((category, index) => (
                        <option key={index} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Insurance Type - Only show if category is selected */}
                  {selectedCategory && (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 text-left">Insurance Type</label>
                      <select 
                        value={selectedInsuranceType}
                        onChange={(e) => {
                          setSelectedInsuranceType(e.target.value)
                          setSelectedCoverageAmount('') // Reset coverage amount when insurance type changes
                        }}
                        className="w-full p-4 rounded-xl bg-white border-2 border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-300 shadow-sm hover:shadow-md"
                      >
                        <option value="">Select Insurance Type</option>
                        {availableInsuranceTypes[selectedCategory]?.map((type, index) => (
                          <option key={index} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Coverage Amount - Only show if both category and insurance type are selected */}
                  {selectedCategory && selectedInsuranceType && (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 text-left">Coverage Amount</label>
                      <select 
                        value={selectedCoverageAmount}
                        onChange={(e) => setSelectedCoverageAmount(e.target.value)}
                        className="w-full p-4 rounded-xl bg-white border-2 border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-300 shadow-sm hover:shadow-md"
                      >
                        <option value="">Select Coverage Amount</option>
                        {coverageAmounts
                          .filter((amount, index, self) => 
                            // Remove duplicates by checking if this is the first occurrence of this value
                            index === self.findIndex(a => a.value === amount.value)
                          )
                          .map((amount, index) => (
                            <option key={amount.value} value={amount.value}>
                              {amount.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  )}
                  <button 
                    disabled={!selectedCategory || !selectedInsuranceType || !selectedCoverageAmount}
                    onClick={() => {
                      if (selectedCategory && selectedInsuranceType && selectedCoverageAmount) {
                        // Track the quote request
                        trackQuoteRequest({
                          policyType: selectedInsuranceType,
                          category: selectedCategory,
                          coverageAmount: selectedCoverageAmount
                        })
                        
                        // Open chat with context
                        const coverageLabel = coverageAmounts.find(a => a.value === parseInt(selectedCoverageAmount))?.label || selectedCoverageAmount
                        openChatWithContext(
                          `I would like to get a quote for ${selectedInsuranceType} under ${selectedCategory} with coverage of ${coverageLabel}.`,
                          'home_quote_form'
                        )
                      }
                    }}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl ${
                      selectedCategory && selectedInsuranceType && selectedCoverageAmount
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Get Quote Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Insurance Solutions for Every Need
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our comprehensive range of insurance products designed to protect 
              what matters most to you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon
              const isHealth = feature.title === 'Health Insurance'
              return (
                <Link 
                  key={index} 
                  to={`/policies?tab=${isHealth ? 'health' : 'life'}`}
                  className="card hover:shadow-lg hover:scale-105 transition-all duration-300 animate-bounce-in block"
                >
                  <div className={`${feature.color} mb-4`}>
                    <Icon className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center lg:text-left">
                Why Choose YourInsurance?
              </h2>
              <p className="text-lg text-gray-600 mb-8 text-center lg:text-left">
                We provide comprehensive insurance solutions with unmatched service quality 
                and customer satisfaction.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 justify-center lg:justify-start">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">50K+</div>
                  <div className="text-gray-600">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">99%</div>
                  <div className="text-gray-600">Claim Settlement</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">24/7</div>
                  <div className="text-gray-600">Customer Support</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">500+</div>
                  <div className="text-gray-600">Hospital Network</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto text-center">
            Join thousands of satisfied customers who trust YourInsurance for their protection needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/policies"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Browse Our Policies
            </Link>
            <button
              onClick={() => openChatWithContext('I have a question about your insurance products and would like to speak with someone.', 'home_cta')}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex items-center justify-center space-x-3">
              <Phone className="h-5 w-5 text-blue-400" />
              <span>+91 98765 43210</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <Mail className="h-5 w-5 text-blue-400" />
              <span>insurance.devrev@gmail.com</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <MapPin className="h-5 w-5 text-blue-400" />
              <span>Bengaluru, Karnataka</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
